import React, { useEffect, useMemo, useState } from "react"
import { useActiveClinic } from "../context/ClinicContext"
import { useClinicSubscription } from "../features/subscriptions/hooks/useClinicSubscription"
import { apiFetch } from "../utils/apiFetch"
import { ui } from "../shared/styles/uiSystem"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

const TOOTH_LEVEL_CONDITIONS = [
  { value: "SANO", label: "Sano" },
  { value: "CORONA", label: "Corona" },
  { value: "ENDODONCIA", label: "Endodoncia" },
  { value: "IMPLANTE", label: "Implante" },
  { value: "AUSENTE", label: "Ausente" },
  { value: "EXTRAIDO", label: "ExtraÃ­do" },
  { value: "FRACTURA", label: "Fractura" },
  { value: "MOVILIDAD", label: "Movilidad" },
  { value: "DESGASTE", label: "Desgaste" },
  { value: "PROTESIS", label: "PrÃ³tesis" },
  { value: "RESTAURACION", label: "RestauraciÃ³n" },
  { value: "OBSERVACION", label: "ObservaciÃ³n" },
]

const SURFACE_LEVEL_CONDITIONS = [
  { value: "CARIES", label: "Caries" },
  { value: "RESTAURACION", label: "RestauraciÃ³n" },
  { value: "RESTAURACION_DEFECTUOSA", label: "RestauraciÃ³n defectuosa" },
  { value: "LESION", label: "LesiÃ³n" },
  { value: "SELLADOR", label: "Sellador" },
  { value: "DESGASTE", label: "Desgaste" },
]

const emptyForm = {
  id: "",
  definitionType: "TOOTH",
  condition: "SANO",
  treatmentId: "",
  suggestedPrice: "",
  notes: "",
  isActive: true,
}

function normalizeDefinitionType(value) {
  const raw = String(value || "").trim().toUpperCase()
  return raw === "SURFACE" ? "SURFACE" : "TOOTH"
}

function getConditionOptions(definitionType) {
  return definitionType === "SURFACE"
    ? SURFACE_LEVEL_CONDITIONS
    : TOOTH_LEVEL_CONDITIONS
}

function getConditionLabel(condition, definitionType) {
  const source = getConditionOptions(normalizeDefinitionType(definitionType))
  return source.find((item) => item.value === condition)?.label || condition || "â€”"
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(Number(value || 0))
}

async function requestFirstOk(paths, buildConfig) {
  let lastError = null

  for (const path of paths) {
    try {
      const config = typeof buildConfig === "function" ? buildConfig(path) : buildConfig
      const res = await apiFetch(`${API_BASE}${path}`, config)
      const data = await res.json().catch(() => null)

      if (res.ok) {
        return { ok: true, data, path }
      }

      lastError = data?.message || `Error ${res.status}`
    } catch (err) {
      lastError = err?.message || "Error de conexiÃ³n"
    }
  }

  return { ok: false, message: lastError || "No se pudo completar la operaciÃ³n" }
}

export default function OdontogramMappingsPage() {
  const { activeClinicId } = useActiveClinic()
  const clinicId = activeClinicId && activeClinicId !== "ALL" ? activeClinicId : ""
  const { subscription } = useClinicSubscription(clinicId)

  const isReadOnly = subscription?.access?.isReadOnly || false
  const readOnlyReason =
    subscription?.access?.reason || "La clÃ­nica estÃ¡ en modo solo lectura."

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [mappings, setMappings] = useState([])
  const [treatments, setTreatments] = useState([])
  const [activeDefinitionType, setActiveDefinitionType] = useState("TOOTH")
  const [query, setQuery] = useState("")
  const [form, setForm] = useState(emptyForm)

  const mappingPaths = useMemo(
    () => ["/odontogram-mappings", "/appointments/odontogram-mappings"],
    []
  )

  const treatmentPaths = useMemo(
    () => ["/treatments", "/appointments/treatments", "/treatment-catalog"],
    []
  )

  const currentConditionOptions = useMemo(
    () => getConditionOptions(form.definitionType),
    [form.definitionType]
  )

  const filteredMappings = useMemo(() => {
    const search = String(query || "").trim().toLowerCase()

    return mappings.filter((item) => {
      const sameType =
        normalizeDefinitionType(item.definitionType) === activeDefinitionType

      if (!sameType) return false

      if (!search) return true

      const treatmentName = getTreatmentName(item)
      const label = getConditionLabel(item.condition, item.definitionType)

      return (
        String(label).toLowerCase().includes(search) ||
        String(treatmentName).toLowerCase().includes(search) ||
        String(item.notes || "").toLowerCase().includes(search)
      )
    })
  }, [mappings, activeDefinitionType, query])

  useEffect(() => {
    if (!clinicId) {
      setMappings([])
      setTreatments([])
      return
    }

    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId])

  useEffect(() => {
    setForm((prev) => {
      const options = getConditionOptions(prev.definitionType)
      const stillExists = options.some((item) => item.value === prev.condition)

      return {
        ...prev,
        condition: stillExists ? prev.condition : options[0]?.value || "",
      }
    })
  }, [form.definitionType])

  const loadAll = async () => {
    try {
      setLoading(true)
      await Promise.all([loadMappings(), loadTreatments()])
    } finally {
      setLoading(false)
    }
  }

  const loadMappings = async () => {
    if (!clinicId) return

    const params = new URLSearchParams({ clinicId })

    const result = await requestFirstOk(mappingPaths, (path) => ({
      method: "GET",
      headers: { "Content-Type": "application/json" },
      path,
    }))

    if (!result.ok) {
      setMappings([])
      return
    }

    const listResult = await requestFirstOk(
      mappingPaths.map((path) => `${path}?${params.toString()}`),
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    )

    if (!listResult.ok) {
      setMappings([])
      return
    }

    const rows = Array.isArray(listResult.data) ? listResult.data : []
    setMappings(rows)
  }

  const loadTreatments = async () => {
    if (!clinicId) return

    const params = new URLSearchParams({ clinicId, limit: "300" })

    const result = await requestFirstOk(
      treatmentPaths.map((path) => `${path}?${params.toString()}`),
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    )

    if (!result.ok) {
      setTreatments([])
      return
    }

    const rows = Array.isArray(result.data)
      ? result.data
      : Array.isArray(result.data?.items)
        ? result.data.items
        : Array.isArray(result.data?.data)
          ? result.data.data
          : []

    setTreatments(rows)
  }

  const resetForm = (definitionType = activeDefinitionType) => {
    const options = getConditionOptions(definitionType)

    setForm({
      ...emptyForm,
      definitionType,
      condition: options[0]?.value || "",
    })
  }

  const handleEdit = (item) => {
    setActiveDefinitionType(normalizeDefinitionType(item.definitionType))

    setForm({
      id: item.id,
      definitionType: normalizeDefinitionType(item.definitionType),
      condition: item.condition || "",
      treatmentId: String(getTreatmentId(item) || ""),
      suggestedPrice:
        item.suggestedPrice != null ? String(item.suggestedPrice) : "",
      notes: item.notes || "",
      isActive: item.isActive !== false,
    })
  }

  const handleDelete = async (item) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura")
      return
    }

    if (!item?.id) return

    const ok = window.confirm("Â¿Eliminar este mapeo?")
    if (!ok) return

    try {
      setSaving(true)

      const result = await requestFirstOk(
        mappingPaths.map((path) => `${path}/${item.id}`),
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      )

      if (!result.ok) {
        alert(result.message || "No se pudo eliminar el mapeo.")
        return
      }

      await loadMappings()
      if (String(form.id || "") === String(item.id)) {
        resetForm(activeDefinitionType)
      }

      alert("Mapeo eliminado correctamente.")
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura")
      return
    }

    if (!clinicId) {
      alert("Selecciona una clÃ­nica vÃ¡lida.")
      return
    }

    if (!form.condition) {
      alert("Selecciona una condiciÃ³n.")
      return
    }

    if (!form.treatmentId) {
      alert("Selecciona un tratamiento.")
      return
    }

    try {
      setSaving(true)

      const payload = {
        clinicId,
        condition: form.condition,
        definitionType: normalizeDefinitionType(form.definitionType),
        treatmentId: form.treatmentId,
        suggestedPrice:
          form.suggestedPrice !== "" ? Number(form.suggestedPrice) : null,
        notes: form.notes?.trim() || null,
        isActive: Boolean(form.isActive),
      }

      const isEdit = Boolean(form.id)

      const result = await requestFirstOk(
        isEdit
          ? mappingPaths.map((path) => `${path}/${form.id}`)
          : mappingPaths,
        {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      )

      if (!result.ok) {
        alert(result.message || "No se pudo guardar el mapeo.")
        return
      }

      await loadMappings()
      resetForm(activeDefinitionType)

      alert(isEdit ? "Mapeo actualizado correctamente." : "Mapeo creado correctamente.")
    } finally {
      setSaving(false)
    }
  }

  const totalToothMappings = mappings.filter(
    (item) => normalizeDefinitionType(item.definitionType) === "TOOTH"
  ).length

  const totalSurfaceMappings = mappings.filter(
    (item) => normalizeDefinitionType(item.definitionType) === "SURFACE"
  ).length

  return (
    <div style={ui.page}>
      <div
        style={{
          ...ui.heroCard,
          marginBottom: 16,
          padding: 18,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at top right, rgba(255,255,255,0.18), transparent 34%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 14 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) auto",
              gap: 12,
              alignItems: "start",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  color: "rgba(255,255,255,0.76)",
                  marginBottom: 6,
                }}
              >
                ConfiguraciÃ³n clÃ­nica
              </div>

              <div
                style={{
                  fontSize: 30,
                  fontWeight: 900,
                  lineHeight: 1.02,
                  letterSpacing: "-0.03em",
                  color: "#ffffff",
                }}
              >
                Mapeos de odontograma
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.84)",
                  marginTop: 6,
                  lineHeight: 1.45,
                }}
              >
                Relaciona cada condiciÃ³n clÃ­nica del odontograma con un tratamiento
                del catÃ¡logo. Todo visible en espaÃ±ol.
              </div>
            </div>

            {isReadOnly ? (
              <div
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: "rgba(254, 242, 242, 0.22)",
                  border: "1px solid rgba(254, 202, 202, 0.45)",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                Solo lectura
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            <MetricCard label="Diente completo" value={totalToothMappings} />
            <MetricCard label="Por superficie" value={totalSurfaceMappings} />
            <MetricCard label="Tratamientos cargados" value={treatments.length} />
          </div>
        </div>
      </div>

      {isReadOnly ? (
        <div
          style={{
            ...ui.dangerCard,
            marginBottom: 16,
            padding: 12,
            fontWeight: 700,
          }}
        >
          ðŸ”’ Modo solo lectura â€” {readOnlyReason}
        </div>
      ) : null}

      {!clinicId ? (
        <div style={{ ...ui.card, padding: 18, color: "#64748b", fontWeight: 700 }}>
          Selecciona una clÃ­nica para configurar los mapeos del odontograma.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(360px, 420px) minmax(0, 1fr)",
            gap: 16,
          }}
        >
          <div
            style={{
              ...ui.card,
              padding: 16,
              display: "grid",
              gap: 14,
              alignContent: "start",
            }}
          >
            <div>
              <div style={ui.sectionTitle}>
                {form.id ? "Editar mapeo" : "Nuevo mapeo"}
              </div>
              <div style={ui.metaText}>
                Define si la condiciÃ³n aplica a diente completo o a superficie.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <SegmentButton
                active={form.definitionType === "TOOTH"}
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    definitionType: "TOOTH",
                    condition: TOOTH_LEVEL_CONDITIONS[0].value,
                  }))
                }}
              >
                Diente completo
              </SegmentButton>

              <SegmentButton
                active={form.definitionType === "SURFACE"}
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    definitionType: "SURFACE",
                    condition: SURFACE_LEVEL_CONDITIONS[0].value,
                  }))
                }}
              >
                Superficie
              </SegmentButton>
            </div>

            <div>
              <label style={ui.label}>CondiciÃ³n</label>
              <select
                value={form.condition}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    condition: e.target.value,
                  }))
                }
                style={ui.input}
                disabled={isReadOnly}
              >
                {currentConditionOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={ui.label}>Tratamiento ligado</label>
              <select
                value={form.treatmentId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    treatmentId: e.target.value,
                  }))
                }
                style={ui.input}
                disabled={isReadOnly}
              >
                <option value="">Selecciona tratamiento</option>
                {treatments.map((item) => (
                  <option key={item.id} value={item.id}>
                    {getTreatmentName(item)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={ui.label}>Precio sugerido</label>
              <input
                type="number"
                value={form.suggestedPrice}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    suggestedPrice: e.target.value,
                  }))
                }
                style={ui.input}
                placeholder="Ej. 800"
                disabled={isReadOnly}
              />
            </div>

            <div>
              <label style={ui.label}>Notas</label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                style={ui.textarea}
                placeholder="Observaciones clÃ­nicas o reglas internas"
                disabled={isReadOnly}
              />
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "#334155",
                fontWeight: 700,
              }}
            >
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
                disabled={isReadOnly}
              />
              Mapeo activo
            </label>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={handleSubmit}
                disabled={saving || isReadOnly}
                style={{
                  ...ui.primaryButton,
                  opacity: saving || isReadOnly ? 0.7 : 1,
                  cursor: saving || isReadOnly ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Guardando..." : form.id ? "Actualizar" : "Crear mapeo"}
              </button>

              <button
                onClick={() => resetForm(activeDefinitionType)}
                disabled={saving}
                style={ui.secondaryButton}
              >
                Limpiar
              </button>
            </div>
          </div>

          <div
            style={{
              ...ui.card,
              padding: 16,
              display: "grid",
              gap: 14,
              alignContent: "start",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) auto",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div>
                <div style={ui.sectionTitle}>Mapeos configurados</div>
                <div style={ui.metaText}>
                  Busca, revisa y edita condiciones en espaÃ±ol.
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <SegmentButton
                  active={activeDefinitionType === "TOOTH"}
                  onClick={() => {
                    setActiveDefinitionType("TOOTH")
                    resetForm("TOOTH")
                  }}
                >
                  Diente completo
                </SegmentButton>

                <SegmentButton
                  active={activeDefinitionType === "SURFACE"}
                  onClick={() => {
                    setActiveDefinitionType("SURFACE")
                    resetForm("SURFACE")
                  }}
                >
                  Superficie
                </SegmentButton>
              </div>
            </div>

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={ui.input}
              placeholder="Buscar condiciÃ³n, tratamiento o nota"
            />

            {loading ? (
              <div style={{ color: "#64748b", fontSize: 14 }}>Cargando mapeos...</div>
            ) : filteredMappings.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 14 }}>
                No hay mapeos para este grupo.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {filteredMappings.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 16,
                      padding: 14,
                      background: "#ffffff",
                      boxShadow: "0 6px 16px rgba(15,23,42,0.04)",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0,1fr) auto",
                        gap: 12,
                        alignItems: "start",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 900,
                              color: "#0f172a",
                            }}
                          >
                            {getConditionLabel(item.condition, item.definitionType)}
                          </div>

                          <TagPill tone="info">
                            {normalizeDefinitionType(item.definitionType) === "TOOTH"
                              ? "Diente completo"
                              : "Superficie"}
                          </TagPill>

                          {item.isActive === false ? (
                            <TagPill tone="neutral">Inactivo</TagPill>
                          ) : (
                            <TagPill tone="success">Activo</TagPill>
                          )}
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            color: "#475569",
                            fontSize: 14,
                            fontWeight: 700,
                          }}
                        >
                          Tratamiento: {getTreatmentName(item)}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            marginTop: 10,
                          }}
                        >
                          <StatPill
                            label="Precio sugerido"
                            value={
                              item.suggestedPrice != null
                                ? formatMoney(item.suggestedPrice)
                                : "Sin precio"
                            }
                          />

                          <StatPill
                            label="Tratamiento ID"
                            value={String(getTreatmentId(item) || "â€”")}
                          />
                        </div>

                        {item.notes ? (
                          <div
                            style={{
                              marginTop: 10,
                              color: "#475569",
                              fontSize: 13,
                              lineHeight: 1.5,
                            }}
                          >
                            {item.notes}
                          </div>
                        ) : null}
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          onClick={() => handleEdit(item)}
                          style={ui.secondaryButton}
                        >
                          Editar
                        </button>

                        <button
                          onClick={() => handleDelete(item)}
                          disabled={saving || isReadOnly}
                          style={{
                            ...ui.secondaryButton,
                            color: "#991b1b",
                            borderColor: "#fecaca",
                            background: "#fff5f5",
                            opacity: saving || isReadOnly ? 0.7 : 1,
                            cursor:
                              saving || isReadOnly ? "not-allowed" : "pointer",
                          }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function getTreatmentId(item) {
  return (
    item?.treatmentId ||
    item?.treatment?.id ||
    item?.linkedTreatmentId ||
    ""
  )
}

function getTreatmentName(item) {
  return (
    item?.treatmentName ||
    item?.treatment?.name ||
    item?.treatment?.title ||
    "Sin tratamiento ligado"
  )
}

function SegmentButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: "8px 12px",
        border: active ? "1px solid #2563eb" : "1px solid #dbe3ef",
        background: active ? "#eff6ff" : "#ffffff",
        color: active ? "#1d4ed8" : "#334155",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  )
}

function MetricCard({ label, value }) {
  return (
    <div
      style={{
        borderRadius: 16,
        padding: 16,
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.16)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "rgba(255,255,255,0.78)",
          marginBottom: 8,
          fontWeight: 700,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontWeight: 900,
          fontSize: 34,
          lineHeight: 1,
          color: "#ffffff",
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </div>
    </div>
  )
}

function TagPill({ children, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: "#f8fafc",
      border: "#cbd5e1",
      color: "#334155",
    },
    info: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    },
    success: {
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
    },
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        padding: "5px 8px",
        borderRadius: 999,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      {children}
    </div>
  )
}

function StatPill({ label, value }) {
  return (
    <div
      style={{
        padding: "7px 10px",
        borderRadius: 12,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b" }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 900, marginTop: 2, color: "#0f172a" }}>
        {value}
      </div>
    </div>
  )
}
